import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  IFollowUpPrompt,
  IMessage,
} from "../../../store/api/aiAssistantApiSlice";
import {MaterialSymbol, SymbolCodepoints} from "react-material-symbols";
import {useState} from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import "./MARKDOWN_STYLES.css";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import rehypeHighlight from "rehype-highlight";
import remarkMath from "remark-math";
import CustomCodeRenderer from "./CustomCodeRenderer";

interface AssistantChatUIProps {
  message: IMessage;
  showActionBar: boolean;
  handleSendMessage: (message: string, messageId?: string) => void;
}
export default function AssistantChatUI({
  message,
  showActionBar,
  handleSendMessage,
}: AssistantChatUIProps) {
  const [isActionBarVisible, setIsActionBarVisible] = useState(showActionBar);
  const isFollowUpPromptsVisible =
    showActionBar &&
    message.followUpPrompts &&
    message.followUpPrompts.length > 0;

  const onFollowUpPromptClick = (prompt: IFollowUpPrompt) => {
    handleSendMessage(prompt.query, prompt.queryId ?? undefined);
  };

  return (
    <Box
      pt={1}
      px={2}
      onMouseEnter={() => setIsActionBarVisible(true)}
      onMouseLeave={() => setIsActionBarVisible(showActionBar || false)}>
      {/* Message */}
      <Typography sx={STYLES.assistantChatText}>
        <div className="markdown">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex, rehypeRaw, rehypeHighlight]}
            components={{
              code: CustomCodeRenderer,
            }}>
            {message.value}
          </ReactMarkdown>
        </div>
      </Typography>

      {/* Action bar */}
      {/* <Stack
        key={"action-bar-" + isActionBarVisible}
        direction={"row"}
        spacing={0.5}
        sx={STYLES.actionBarContainer(isActionBarVisible)}>
        <ActionIconButton tooltipText="Copy" icon={"content_copy"} />
        <ActionIconButton tooltipText="Good response" icon={"thumb_up"} />
        <ActionIconButton tooltipText="Bad response" icon={"thumb_down"} />
      </Stack> */}
      {isFollowUpPromptsVisible && (
        <List sx={{pt: 2}}>
          <RelatedQuestionsHeader />
          {message.followUpPrompts?.map(prompt => (
            <FollowUpPromptItem
              key={prompt.query}
              prompt={prompt}
              onClick={() => onFollowUpPromptClick(prompt)}
            />
          ))}
        </List>
      )}
    </Box>
  );
}

interface ActionIconButtonProps {
  tooltipText: string;
  icon: SymbolCodepoints;
  onClick?: () => void;
}
function ActionIconButton({tooltipText, icon, onClick}: ActionIconButtonProps) {
  return (
    <Tooltip title={tooltipText} arrow>
      <IconButton
        size={"small"}
        sx={{color: "primary.contrastText", borderRadius: "8px"}}
        onClick={onClick}>
        <MaterialSymbol
          icon={icon}
          style={{color: "inherit", fontWeight: 500, fontSize: "14px"}}
        />
      </IconButton>
    </Tooltip>
  );
}

function FollowUpPromptItem({
  prompt,
  onClick,
}: {
  prompt: IFollowUpPrompt;
  onClick: (prompt: IFollowUpPrompt) => void;
}) {
  return (
    <ListItem
      onClick={() => onClick(prompt)}
      divider
      sx={STYLES.followUpPromptListItem}
      secondaryAction={<MaterialSymbol icon={"add"} size={20} />}>
      <ListItemText
        primaryTypographyProps={STYLES.followUpPromptText}
        primary={prompt.query}
      />
    </ListItem>
  );
}

function RelatedQuestionsHeader() {
  return (
    <ListItem
      key={"related-questions-header"}
      // divider
      sx={STYLES.followUpPromptHeaderListItem}>
      <ListItemIcon>
        <MaterialSymbol icon={"question_answer"} size={18} fill />
      </ListItemIcon>
      <ListItemText
        primaryTypographyProps={STYLES.relatedQuestionsHeaderText}
        primary={"Related questions"}
      />
    </ListItem>
  );
}

const STYLES = {
  // Assistant chat
  assistantChatText: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: "1.25rem",
  } as const,

  // Action bar
  actionBarContainer: (isActionBarVisible: boolean) =>
    ({
      justifyContent: "flex-end",
      opacity: isActionBarVisible ? 1 : 0,
      transition: "opacity 0.2s ease-in-out",
    }) as const,

  // Follow up prompt
  followUpPromptListItem: {
    px: 0,
    "& .MuiListItemSecondaryAction-root": {
      color: "inherit",
      right: 0,
      width: "28px",
    },
    "&:hover": {
      color: "primary.dark",
      cursor: "pointer",
    },
  } as const,
  followUpPromptText: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: "1.5rem",
  } as const,
  followUpPromptHeaderListItem: {
    px: 0,
    py: 0,
    "& .MuiListItemIcon-root": {
      minWidth: "24px",
      color: "primary.contrastText",
    },
  } as const,
  relatedQuestionsHeaderText: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: "1.5rem",
  },
};
